import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart
} from 'recharts';
import {
  Card, Row, Col, Statistic, Progress, Alert, Button, Space,
  Tabs, Select, DatePicker, Spin, Empty, Tag, Avatar, List
} from 'antd';
import {
  DollarOutlined, CalendarOutlined, UserOutlined, ShoppingCartOutlined,
  RiseOutlined, FallOutlined, ArrowUpOutlined, ArrowDownOutlined,
  ReloadOutlined, DownloadOutlined, BulbOutlined, WarningOutlined,
  CheckCircleOutlined, InfoCircleOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Option } = Select;

interface AnalyticsData {
  kpis: {
    totalRevenue: number;
    totalBookings: number;
    confirmedBookings: number;
    totalCustomers: number;
    avgBookingValue: number;
    bookingConversionRate: number;
    customerLifetimeValue: number;
  };
  charts: {
    revenueByDay: any[];
    bookingStatusData: any[];
    topServices: any[];
    customerSegments: any[];
    productPerformance: any[];
  };
  insights: any[];
  metadata: {
    period: string;
    generatedAt: string;
  };
}

interface PredictionData {
  trends: any[];
  growth: {
    revenue: number;
    bookings: number;
  };
  predictions: {
    nextMonthRevenue: number;
    nextMonthBookings: number;
  };
}

const BookingAnalytics: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [predictionData, setPredictionData] = useState<PredictionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadAnalytics();
  }, [token, navigate]);

  const loadAnalytics = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const [analyticsRes, predictionsRes] = await Promise.all([
        apiFetch(`${API_URL}/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        apiFetch(`${API_URL}/analytics/predictions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (analyticsRes.ok) {
        const analytics = await analyticsRes.json();
        setAnalyticsData({
          ...analytics,
          kpis: {
            totalRevenue: 0,
            totalBookings: 0,
            confirmedBookings: 0,
            totalCustomers: 0,
            avgBookingValue: 0,
            bookingConversionRate: 0,
            customerLifetimeValue: 0,
            ...analytics.kpis,
          },
          charts: {
            revenueByDay: [],
            bookingStatusData: [],
            topServices: [],
            customerSegments: [],
            productPerformance: [],
            ...analytics.charts,
          },
        });
      }

      if (predictionsRes.ok) {
        const predictions = await predictionsRes.json();
        setPredictionData({
          trends: predictions.trends || [],
          growth: { revenue: 0, bookings: 0, ...predictions.growth },
          predictions: {
            nextMonthRevenue: 0,
            nextMonthBookings: 0,
            ...predictions.predictions,
          },
        });
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalytics();
  };

  const exportData = () => {
    if (!analyticsData) return;

    const data = {
      ...analyticsData,
      exportedAt: new Date().toISOString(),
      dateRange: dateRange.map(d => d.format('YYYY-MM-DD'))
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${dayjs().format('YYYY-MM-DD')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Empty
          description="No analytics data available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
        <Button type="primary" onClick={loadAnalytics} style={{ marginTop: 16 }}>
          Try Again
        </Button>
      </div>
    );
  }

  const { kpis, charts, insights } = analyticsData;

  // KPI Cards with trend indicators
  const kpiCards = [
    {
      title: 'Total Revenue',
      value: kpis.totalRevenue,
      prefix: '£',
      icon: <DollarOutlined />,
      color: '#10b981',
      trend: predictionData?.growth.revenue || 0
    },
    {
      title: 'Total Bookings',
      value: kpis.totalBookings,
      icon: <CalendarOutlined />,
      color: '#3b82f6',
      trend: predictionData?.growth.bookings || 0
    },
    {
      title: 'Confirmed Bookings',
      value: kpis.confirmedBookings,
      icon: <CheckCircleOutlined />,
      color: '#8b5cf6',
      suffix: `(${ (kpis.bookingConversionRate ?? 0).toFixed(1)}% conversion)`
    },
    {
      title: 'Active Customers',
      value: kpis.totalCustomers,
      icon: <UserOutlined />,
      color: '#f59e0b',
      suffix: `Avg: £${(kpis.customerLifetimeValue ?? 0).toFixed(0)} LTV`
    }
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>
              📊 Smart Analytics Dashboard
            </h1>
            <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>
              Intelligent insights for your business performance
            </p>
          </Col>
          <Col>
            <Space>
              <RangePicker
                value={dateRange}
                onChange={(dates) => dates && setDateRange(dates)}
                disabled
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={refreshing}
              >
                Refresh
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportData}
              >
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Intelligent Insights */}
      {insights.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            {insights.map((insight, index) => (
              <Col xs={24} lg={12} key={index}>
                <Alert
                  message={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>{insight.icon}</span>
                      <span style={{ fontWeight: 600 }}>{insight.title}</span>
                    </div>
                  }
                  description={insight.message}
                  type={insight.type === 'success' ? 'success' : insight.type === 'warning' ? 'warning' : 'info'}
                  showIcon
                  style={{ borderRadius: '8px' }}
                />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {kpiCards.map((kpi, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card
              style={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                background: `linear-gradient(135deg, ${kpi.color}15, ${kpi.color}05)`
              }}
            >
              <Statistic
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar
                      size="small"
                      style={{ backgroundColor: kpi.color }}
                      icon={kpi.icon}
                    />
                    {kpi.title}
                  </div>
                }
                value={kpi.value}
                prefix={kpi.prefix}
                suffix={kpi.suffix}
                valueStyle={{ color: kpi.color, fontSize: '24px', fontWeight: 700 }}
              />
              {kpi.trend !== undefined && (
                <div style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: kpi.trend >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {kpi.trend >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  {Math.abs(kpi.trend).toFixed(1)}% from last month
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {/* Charts Tabs */}
      <Card
        style={{
          borderRadius: '12px',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}
      >
        <Tabs defaultActiveKey="1" type="card">
          <TabPane tab="📈 Revenue & Bookings" key="1">
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={16}>
                <Card title="Revenue Trend (Last 7 Days)" size="small">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={charts.revenueByDay}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`£${value}`, 'Revenue']} />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="Booking Status" size="small">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={charts.bookingStatusData}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {charts.bookingStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="🎯 Services & Customers" key="2">
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <Card title="Top Services" size="small">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={charts.topServices}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="service" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="Customer Segments" size="small">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={charts.customerSegments}
                        dataKey="count"
                        nameKey="segment"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {charts.customerSegments.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="📦 Product Performance" key="3">
            <Card title="Top Performing Products" size="small">
              <List
                dataSource={charts.productPerformance}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: '#f59e0b' }}>📦</Avatar>}
                      title={item.name}
                      description={`Category: ${item.category} • Stock: ${item.stock}`}
                    />
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 600, color: '#10b981' }}>
                        £{item.revenue.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {item.sold} units sold
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </TabPane>

          <TabPane tab="🔮 Predictions" key="4">
            {predictionData && (
              <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                  <Card title="Trend Analysis" size="small">
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={predictionData.trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="left" dataKey="bookings" fill="#3b82f6" name="Bookings" />
                        <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" name="Revenue (£)" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="Next Month Predictions" size="small">
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <div style={{ marginBottom: '24px' }}>
                        <div style={{ fontSize: '48px', fontWeight: 700, color: '#10b981', marginBottom: '8px' }}>
                          £{(predictionData.predictions.nextMonthRevenue ?? 0).toLocaleString()}
                        </div>
                        <div style={{ color: '#6b7280' }}>Predicted Revenue</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '36px', fontWeight: 700, color: '#3b82f6', marginBottom: '8px' }}>
                          {predictionData.predictions.nextMonthBookings}
                        </div>
                        <div style={{ color: '#6b7280' }}>Predicted Bookings</div>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default BookingAnalytics;
